import { provideGlobalGridOptions } from 'ag-grid-community';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_CHIPS_DEFAULT_OPTIONS } from '@angular/material/chips';
import { DateAdapter, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarConfig, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { MissingTranslationHandler, UserPreferenceProvider } from '@app/shared';
import { TRANSLOCO_MISSING_HANDLER } from '@jsverse/transloco';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { ErrorInterceptor } from './app/_interceptors/error.interceptor';
import { JwtInterceptor } from './app/_interceptors/jwt.interceptor';
import { AUTH_PROVIDER } from './app/_services/auth/auth-provider.port';
import { CognitoAuthProviderService } from './app/_services/auth/cognito-auth-provider.service';
import { DateAdapter as AbcdjDateAdapter } from './app/_services/date.adapter';
import { SongFormService } from './app/_services/song-form.service';
import { SongService, TRACK_METADATA_PROVIDER } from './app/_services/song.service';
import { UserService } from './app/_services/user.service';
import { RootStoreModule } from './app/_state';
import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { StatusChangesGuard } from './app/guard/status-changes.guard';
import { TranslocoRootModule } from './app/i18n/transloco-root.module';
import { environment } from './environments/environment';

if (environment.production) {
  enableProdMode();
}

const BOOT_SPLASH_MIN_VISIBLE_MS = 400;

const hideBootSplashWhenReady = (startTime: number): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const splash = document.getElementById('boot-splash');
  if (!splash) {
    return;
  }

  const elapsed = Date.now() - startTime;
  const remaining = Math.max(0, BOOT_SPLASH_MIN_VISIBLE_MS - elapsed);

  window.setTimeout(() => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        splash.remove();
      });
    });
  }, remaining);
};

const bootstrapStart = Date.now();

// Use AG Grid legacy CSS themes (v32 style) instead of the new v33 Theming API
provideGlobalGridOptions({ theme: 'legacy' });

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    importProvidersFrom(AppRoutingModule, BrowserModule, CommonModule, FormsModule, ReactiveFormsModule, ClipboardModule, OverlayModule, MatAutocompleteModule, MatButtonModule, MatButtonToggleModule, MatCheckboxModule, MatDialogModule, MatDividerModule, MatExpansionModule, MatIconModule, MatInputModule, MatListModule, MatNativeDateModule, MatSelectModule, MatSidenavModule, MatSnackBarModule, MatToolbarModule, MatTooltipModule, TranslocoRootModule, RootStoreModule, !environment.production ? StoreDevtoolsModule.instrument({ connectInZone: true }) : []),
    SongFormService,
    StatusChangesGuard,
    {
      provide: AUTH_PROVIDER,
      useExisting: CognitoAuthProviderService,
    },
    { provide: UserPreferenceProvider, useExisting: UserService },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    {
      provide: DateAdapter,
      useClass: AbcdjDateAdapter,
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'en-GB',
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        width: '600px',
        panelClass: 'hdis-dialog-container',
        hasBackdrop: true,
      },
    },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        duration: 5000,
        horizontalPosition: 'end',
      } as MatSnackBarConfig,
    },
    {
      provide: MAT_CHIPS_DEFAULT_OPTIONS,
      useValue: {
        separatorKeyCodes: [ENTER, COMMA],
      },
    },
    {
      provide: TRANSLOCO_MISSING_HANDLER,
      useClass: MissingTranslationHandler,
    },
    {
      provide: TRACK_METADATA_PROVIDER,
      useExisting: SongService,
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
  ],
})
  .then(() => {
    hideBootSplashWhenReady(bootstrapStart);
  })
  .catch((err) => console.error(err));
